import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, getDocs, addDoc, orderBy, limit, limitToLast, startAfter, startAt, endBefore, getCountFromServer, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { Phone, MessageCircle, Package, LogOut, Save, X, FileText, Search, Filter, Menu, Star, UserRound, PlusCircle, Bookmark } from 'lucide-react';
import { auth, db } from './firebase';
import DashboardNav from './DashboardNav';
import CallHistoryButton from './CallHistory';
import { NextCallBadge, NextCallPicker } from './CallFollowUp';
import CustomerProfileModal, { daysFromToday, fmtYMD, tierOf, TierBadge, openCreateOrder } from './CustomerProfileModal';
import AddReviewModal from './AddReviewModal';

const STATUS = 'Delivered';
const STORE_KEY = `orderPageState:${STATUS}`;
const EMPTY_FILTERS = { phoneSearch: '', selectedMonth: '', selectedYear: '', startDate: '', endDate: '' };
const loadSaved = () => {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
};
const persist = (data) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
};
const toDate = (ts) => (ts?.toDate ? ts.toDate() : new Date(ts));
const firstMillis = (list) => (list && list[0] && list[0].created_at ? toDate(list[0].created_at).getTime() : null);

// Filter → Firestore query constraints (server-side)
const buildConstraints = (f) => {
    const c = [where('status', '==', STATUS)];
    const term = (f.phoneSearch || '').trim();
    if (term) {
        // phone prefix search (date/month filter এ ছাড়া) — index: status ASC, customerInfo.phone ASC
        c.push(where('customerInfo.phone', '>=', term), where('customerInfo.phone', '<=', term + '\uf8ff'), orderBy('customerInfo.phone'));
        return { c, phoneMode: true };
    }
    let start = null;
    let end = null;
    const bump = (s, e) => {
        if (s && (!start || s > start)) start = s;
        if (e && (!end || e < end)) end = e;
    };
    if (f.selectedYear) {
        const y = parseInt(f.selectedYear);
        if (f.selectedMonth) {
            const m = parseInt(f.selectedMonth) - 1;
            bump(new Date(y, m, 1), new Date(y, m + 1, 0, 23, 59, 59, 999));
        } else {
            bump(new Date(y, 0, 1), new Date(y, 11, 31, 23, 59, 59, 999));
        }
    }
    if (f.startDate) bump(new Date(f.startDate + 'T00:00:00'), null);
    if (f.endDate) bump(null, new Date(f.endDate + 'T23:59:59.999'));
    if (start) c.push(where('created_at', '>=', Timestamp.fromDate(start)));
    if (end) c.push(where('created_at', '<=', Timestamp.fromDate(end)));
    c.push(orderBy('created_at', 'desc'));
    return { c, phoneMode: false };
};

function DeliveredOrderPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalDelivered, setTotalDelivered] = useState(0);
    const saved = useMemo(() => loadSaved(), []);
    const [currentPage, setCurrentPage] = useState(saved.page || 1);
    const [totalFiltered, setTotalFiltered] = useState(0);
    const [marked, setMarked] = useState(saved.marked || null);
    const [scrollToId, setScrollToId] = useState(null);
    const [authed, setAuthed] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);
    const [filters, setFilters] = useState({ ...EMPTY_FILTERS, ...(saved.f || {}) });
    const firstDocRef = useRef(null);
    const lastDocRef = useRef(null);
    const reqRef = useRef(0);
    const skipFilterFetchRef = useRef(false);
    const didInitRef = useRef(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [profileOrder, setProfileOrder] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [leadStatus, setLeadStatus] = useState('');
    const [nextCallDate, setNextCallDate] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [callNotes, setCallNotes] = useState([]);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [reviewOrder, setReviewOrder] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);

    // Filter states (এক জায়গায়)
    const { phoneSearch, selectedMonth, selectedYear, startDate, endDate } = filters;

    const navigate = useNavigate();
    const ordersPerPage = 10;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserEmail(user.email);
                setAuthed(true);
            } else {
                navigate('/login');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // মোট Delivered (sidebar count) — count query, প্রতি ১০০০ entry তে ১ read
    useEffect(() => {
        if (!authed) return;
        getCountFromServer(query(collection(db, 'orders'), where('status', '==', STATUS)))
            .then((cs) => setTotalDelivered(cs.data().count))
            .catch((e) => console.error('Count error:', e));
    }, [authed]);

    // শুধু ১ পেজ (১০টা doc) আনে। mode: first | next | prev | restore
    const fetchPage = async (mode, opts = {}) => {
        const f = opts.filters || filters;
        const reqId = ++reqRef.current;
        setLoading(true);
        try {
            const { c, phoneMode } = buildConstraints(f);
            const coll = collection(db, 'orders');
            let q;
            let newPage = 1;
            if (mode === 'next' && lastDocRef.current) {
                q = query(coll, ...c, startAfter(lastDocRef.current), limit(ordersPerPage));
                newPage = currentPage + 1;
            } else if (mode === 'prev' && firstDocRef.current) {
                q = query(coll, ...c, endBefore(firstDocRef.current), limitToLast(ordersPerPage));
                newPage = Math.max(1, currentPage - 1);
            } else if (mode === 'restore' && !phoneMode && opts.millis) {
                q = query(coll, ...c, startAt(Timestamp.fromMillis(opts.millis)), limit(ordersPerPage));
                newPage = opts.page || 1;
            } else {
                q = query(coll, ...c, limit(ordersPerPage));
            }
            let snap = await getDocs(q);
            if (snap.empty && mode === 'restore') {
                // সেভ করা অবস্থান আর নেই → প্রথম পেজ
                snap = await getDocs(query(coll, ...c, limit(ordersPerPage)));
                newPage = 1;
            }
            if (reqId !== reqRef.current) return;
            firstDocRef.current = snap.docs[0] || null;
            lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
            setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setCurrentPage(newPage);
            setLoadedOnce(true);
            if (opts.count) {
                const cs = await getCountFromServer(query(coll, ...c));
                if (reqId !== reqRef.current) return;
                setTotalFiltered(cs.data().count);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            if (String(error?.message || '').includes('index')) {
                alert('Firestore index দরকার — browser console এর লিংকে ক্লিক করে index তৈরি করুন।');
            }
        } finally {
            if (reqId === reqRef.current) setLoading(false);
        }
    };

    // filter বদলালে (বা প্রথমবার) ১ম পেজ + count আনে; প্রথমবার সেভ করা পেজ ফিরিয়ে আনে
    useEffect(() => {
        if (!authed) return;
        if (skipFilterFetchRef.current) {
            skipFilterFetchRef.current = false;
            return;
        }
        const first = !didInitRef.current;
        const t = setTimeout(() => {
            didInitRef.current = true;
            if (first && saved.page > 1 && saved.startMillis && !filters.phoneSearch.trim()) {
                fetchPage('restore', { page: saved.page, millis: saved.startMillis, count: true });
            } else {
                fetchPage('first', { count: true });
            }
        }, first ? 0 : 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authed, JSON.stringify(filters)]);

    const totalPages = Math.max(1, Math.ceil(totalFiltered / ordersPerPage));
    const page = Math.min(Math.max(1, currentPage), totalPages);
    const startIndex = orders.length === 0 ? 0 : (page - 1) * ordersPerPage + 1;
    const endIndex = (page - 1) * ordersPerPage + orders.length;
    const markedId = marked?.id || null;

    // পেজ/ফিল্টার/মার্ক মনে রাখে (refresh বা অন্য পেজ ঘুরে এলেও)
    useEffect(() => {
        if (!loadedOnce) return;
        persist({ page: currentPage, startMillis: firstMillis(orders), marked, f: filters });
    }, [loadedOnce, currentPage, orders, marked, filters]);

    // মার্ক করা row তে scroll
    useEffect(() => {
        if (!scrollToId || loading) return;
        const el = document.getElementById(`order-row-${scrollToId}`);
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            setScrollToId(null);
        }
    }, [scrollToId, orders, loading]);

    useEffect(() => {
        if (loadedOnce && markedId) setScrollToId(markedId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedOnce]);

    const onFilter = (key) => (e) => {
        const v = e.target.value;
        setFilters((prev) => {
            const next = { ...prev, [key]: v };
            // মাস বাছলে বছর না থাকলে চলতি বছর ধরা হয় (server-side range এর জন্য)
            if (key === 'selectedMonth' && v && !next.selectedYear) next.selectedYear = String(new Date().getFullYear());
            return next;
        });
    };

    const clearFilters = () => setFilters(EMPTY_FILTERS);

    const markOrder = (orderId) => setMarked({ id: orderId, page: currentPage, millis: firstMillis(orders), f: filters });
    const toggleMark = (orderId) => (markedId === orderId ? setMarked(null) : markOrder(orderId));

    const goToMarked = () => {
        if (!marked) return;
        const same = JSON.stringify(marked.f) === JSON.stringify(filters);
        if (!same) {
            skipFilterFetchRef.current = true;
            setFilters(marked.f);
        }
        if (same && marked.page === page) {
            setScrollToId(marked.id);
            return;
        }
        fetchPage(marked.page > 1 && marked.millis ? 'restore' : 'first', { filters: marked.f, page: marked.page, millis: marked.millis, count: !same });
        setScrollToId(marked.id);
    };

    const fetchCallNotes = async (orderId) => {
        setLoadingNotes(true);
        try {
            const notesQuery = query(
                collection(db, 'orders', orderId, 'callNotes'),
                orderBy('timestamp', 'desc')
            );
            const notesSnapshot = await getDocs(notesQuery);
            const notes = [];
            notesSnapshot.forEach((doc) => {
                notes.push({ id: doc.id, ...doc.data() });
            });
            setCallNotes(notes);
        } catch (error) {
            console.error('Error fetching notes:', error);
            setCallNotes([]);
        } finally {
            setLoadingNotes(false);
        }
    };

    const handleShowNotes = async (order) => {
        setSelectedOrder(order);
        markOrder(order.id);
        setShowNotesModal(true);
        await fetchCallNotes(order.id);
    };

    const handleNextPage = () => {
        if (page < totalPages) {
            fetchPage('next');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePrevPage = () => {
        if (page > 1) {
            fetchPage('prev');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePhoneCall = (phone) => {
        window.location.href = `sip:${phone}`;
    };

    const handleWhatsApp = (phone) => {
        const cleanPhone = phone.replace(/[^0-9]/g, '');
        window.open(`https://wa.me/${cleanPhone}`, '_blank');
    };

    const saveCallNote = async () => {
        if (!selectedOrder || !noteText.trim()) {
            alert('Please enter a note');
            return;
        }

        try {
            const callNotesRef = collection(db, 'orders', selectedOrder.id, 'callNotes');
            await addDoc(callNotesRef, {
                email: userEmail,
                note: noteText,
                timestamp: Timestamp.now(),
                leadStatus: leadStatus || 'followup',
                nextCallDate: nextCallDate || null
            });

            const orderUpdate = {};
            if (leadStatus) orderUpdate.leadStatus = leadStatus;
            if (nextCallDate) {
                // নতুন Next Call তারিখ দিলে সেট হবে (Call Report → Next Call Due এ দেখা যাবে)
                orderUpdate.nextCall = {
                    date: nextCallDate,
                    status: 'pending',
                    setBy: userEmail,
                    setAt: Timestamp.now()
                };
            } else if (selectedOrder.nextCall?.status === 'pending') {
                // কল দেওয়া হয়েছে, নতুন তারিখ নেই → আগের pending Next Call "done"
                orderUpdate['nextCall.status'] = 'done';
                orderUpdate['nextCall.doneAt'] = Timestamp.now();
                orderUpdate['nextCall.doneBy'] = userEmail;
            }
            if (Object.keys(orderUpdate).length > 0) {
                const orderRef = doc(db, 'orders', selectedOrder.id);
                await updateDoc(orderRef, orderUpdate);
            }

            // refetch না করে শুধু ওই order টা লোকালি আপডেট — পেজ/ফিল্টার যেমন ছিল তেমনই থাকবে
            const localPatch = {};
            if (leadStatus) localPatch.leadStatus = leadStatus;
            if (nextCallDate) {
                localPatch.nextCall = { date: nextCallDate, status: 'pending', setBy: userEmail, setAt: Timestamp.now() };
            } else if (selectedOrder.nextCall?.status === 'pending') {
                localPatch.nextCall = { ...selectedOrder.nextCall, status: 'done', doneBy: userEmail, doneAt: Timestamp.now() };
            }
            const savedId = selectedOrder.id;
            setOrders(prev => prev.map(o => (o.id === savedId ? { ...o, ...localPatch } : o)));
            markOrder(savedId);

            alert('Note saved successfully!');
            setNoteText('');
            setLeadStatus('');
            setNextCallDate('');
            setSelectedOrder(null);
        } catch (error) {
            console.error('Error saving note:', error);
            alert('Failed to save note');
        }
    };

    // রিভিউ যোগ হলে ওই order এর cartItems (review_added) লিস্টে আপডেট করে দেয়
    const handleReviewSaved = (orderId, cartItems) => {
        setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, cartItems } : o)));
    };

    // Customer Profile / Repeat reminder সেভ হলে ওই order টা লিস্টে আপডেট করে দেয়
    const handleProfileSaved = (orderId, patch) => {
        setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, ...patch } : o)));
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            let date;
            if (timestamp.toDate) {
                date = timestamp.toDate();
            } else {
                date = new Date(timestamp);
            }

            const now = new Date();
            const diffTime = Math.abs(now - date);
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays === 0) {
                return 'Today';
            } else if (diffDays === 1) {
                return 'A day ago';
            } else if (diffDays < 7) {
                return `${diffDays} days ago`;
            } else if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                return weeks === 1 ? 'A week ago' : `${weeks} weeks ago`;
            } else {
                const month = date.toLocaleString('en-US', { month: 'long' });
                const year = date.getFullYear();
                return `${month} ${year}`;
            }
        } catch (error) {
            return 'N/A';
        }
    };

    const formatFullDate = (timestamp) => {
        if (!timestamp) return '';
        try {
            let date;
            if (timestamp.toDate) {
                date = timestamp.toDate();
            } else {
                date = new Date(timestamp);
            }
            const day = date.getDate();
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (error) {
            return '';
        }
    };

    const getLeadStatusBadge = (status) => {
        if (!status) return null;

        const statusConfig = {
            followup: { bg: 'bg-yellow-100 text-yellow-700', text: 'Follow Up' },
            converted: { bg: 'bg-green-100 text-green-700', text: 'Converted' },
            next_call: { bg: 'bg-blue-100 text-blue-700', text: 'Next Call' },
            not_interested: { bg: 'bg-red-100 text-red-700', text: 'Not Interested' }
        };

        const config = statusConfig[status] || { bg: 'bg-gray-100', text: status };

        return (
            <span className={`text-xs px-2 py-1 rounded ${config.bg}`}>
                {config.text}
            </span>
        );
    };

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const months = [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    return (
        <div className="flex min-h-screen bg-gray-50 text-gray-900">
            {/* Sidebar */}

            <DashboardNav
                sidebarOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                counts={{ delivered: totalDelivered }}
            />


            {/* Main Content */}
            <div className="flex-1 min-w-0 p-6 overflow-auto">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 hover:bg-orange-50 rounded-lg transition"
                            >
                                <Menu size={24} />
                            </button>
                            <div>
                                <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-green-400 to-emerald-600 bg-clip-text text-transparent">
                                    Delivered Orders Management
                                </h1>
                                <p className="text-gray-500 mt-1">
                                    Total: <span className="text-green-600 font-semibold">{totalDelivered}</span> |
                                    Showing: <span className="text-orange-600">{startIndex}-{endIndex}</span>
                                </p>
                                <p className="text-sm text-gray-500">Logged in as: {userEmail}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg transition duration-200"
                        >
                            <LogOut size={20} />
                            Logout
                        </button>
                    </div>



                    {/* Filters */}
                    <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
                        <div className="flex items-center gap-2 mb-4">
                            <Filter size={20} className="text-orange-600" />
                            <h3 className="text-lg font-semibold">Filters</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Search size={16} className="inline mr-1" />
                                    Phone Number
                                </label>
                                <input
                                    type="text"
                                    value={phoneSearch}
                                    onChange={onFilter('phoneSearch')}
                                    placeholder="Phone এর শুরুর অংশ লিখুন..."
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                                <select
                                    value={selectedMonth}
                                    onChange={onFilter('selectedMonth')}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                >
                                    <option value="">All Months</option>
                                    {months.map(month => (
                                        <option key={month.value} value={month.value}>{month.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                                <select
                                    value={selectedYear}
                                    onChange={onFilter('selectedYear')}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                >
                                    <option value="">All Years</option>
                                    {years.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-end">
                                <button
                                    onClick={clearFilters}
                                    className="w-full px-4 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition duration-200"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={onFilter('startDate')}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={onFilter('endDate')}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Marked order banner */}
                    {marked && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex flex-wrap items-center justify-between gap-2">
                            <p className="text-amber-800 text-sm">
                                <Bookmark size={16} className="inline mr-1" />
                                শেষ কাজ করা order: <b>Page {marked.page}</b>
                            </p>
                            <div className="flex gap-2">
                                <button onClick={goToMarked} className="px-3 py-1 text-sm bg-amber-400 text-white hover:bg-amber-500 rounded-lg">
                                    সেখানে যান
                                </button>
                                <button onClick={() => setMarked(null)} className="px-3 py-1 text-sm bg-white border border-amber-300 text-amber-700 hover:bg-amber-100 rounded-lg">
                                    Mark মুছুন
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Results Info */}
                    {(phoneSearch || selectedMonth || selectedYear || startDate || endDate) && (
                        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                            <p className="text-orange-700">
                                Filter অনুযায়ী মোট {totalFiltered}টা order{phoneSearch.trim() ? ' (Phone search এ month/date filter প্রযোজ্য নয়)' : ''}
                            </p>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-500"></div>
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-16">
                            <p className="text-gray-500 text-xl">No orders found matching your filters</p>
                        </div>
                    ) : (
                        <>
                            {/* Orders Table */}
                            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Order ID</th>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Customer Info</th>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Product</th>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Amount</th>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Lead Status</th>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {orders.map((order) => (
                                                <tr
                                                    key={order.id}
                                                    id={`order-row-${order.id}`}
                                                    className={`transition duration-150 ${markedId === order.id ? 'bg-amber-50 ring-2 ring-inset ring-amber-400' : 'hover:bg-orange-50'}`}
                                                >
                                                    <td className="px-4 py-4">
                                                        <div className="text-sm font-mono text-orange-600">
                                                            {order.orderId?.substring(0, 8)}...
                                                        </div>
                                                        <div className="text-xs text-gray-500">{order.orderSource || 'N/A'}</div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="text-sm font-semibold">{order.customerInfo?.name || 'N/A'}</div>
                                                        <div className="text-xs text-gray-500">{order.customerInfo?.phone || 'N/A'}</div>
                                                        <div className="text-xs text-gray-500 max-w-xs truncate">
                                                            {order.customerInfo?.address || 'N/A'}
                                                        </div>
                                                        {order.customerInfo?.notes && (
                                                            <div className="text-xs text-yellow-600 mt-1">
                                                                Note: {order.customerInfo.notes}
                                                            </div>
                                                        )}
                                                        {(tierOf(order.customerProfile) || order.customerProfile?.profession || order.repeatReminder) && (
                                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                                <TierBadge tier={tierOf(order.customerProfile)} />
                                                                {order.customerProfile?.profession && (
                                                                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                                                        {order.customerProfile.profession === 'অন্যান্য' && order.customerProfile.professionOther
                                                                            ? order.customerProfile.professionOther
                                                                            : order.customerProfile.profession}
                                                                    </span>
                                                                )}
                                                                {order.repeatReminder && (
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${order.repeatReminder.status === 'done'
                                                                        ? 'bg-green-100 text-green-700'
                                                                        : daysFromToday(order.repeatReminder.date) < 0
                                                                            ? 'bg-red-100 text-red-700'
                                                                            : 'bg-orange-100 text-orange-700'}`}>
                                                                        {order.repeatReminder.status === 'done' ? '✓ Repeat done' : `🔔 Repeat: ${fmtYMD(order.repeatReminder.date)}`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {(order.customerProfile?.preferredProducts || []).length > 0 && (
                                                            <div className="text-xs text-rose-600 mt-1 max-w-xs truncate">
                                                                ❤️ {order.customerProfile.preferredProducts.map((x) => x.name).join(', ')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        {order.cartItems && order.cartItems[0] ? (
                                                            <div className="flex items-center gap-3">
                                                                <img
                                                                    src={order.cartItems[0].image}
                                                                    alt={order.cartItems[0].name}
                                                                    className="w-12 h-12 rounded object-cover"
                                                                    onError={(e) => {
                                                                        e.target.src = 'https://via.placeholder.com/48';
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div className="text-sm font-medium max-w-xs truncate">
                                                                        {order.cartItems[0].name}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        Qty: {order.cartItems[0].quantity} | ৳{order.cartItems[0].price}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-500">No product</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="text-sm font-semibold text-green-600">৳{order.totalAmount || 0}</div>
                                                        <div className="text-xs text-gray-500">COD: ৳{order.codForCourier || 0}</div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="text-sm">{formatDate(order.created_at)}</div>
                                                        <div className="text-xs text-gray-500">{formatFullDate(order.created_at)}</div>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm">
                                                        {getLeadStatusBadge(order.leadStatus)}
                                                        <div className="mt-1.5"><NextCallBadge order={order} /></div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => toggleMark(order.id)}
                                                                className={`p-2 rounded-lg transition duration-200 ${markedId === order.id ? 'bg-amber-400 text-white' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                                                                title="এখানে কাজ করছি — Mark করুন"
                                                            >
                                                                <Bookmark size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handlePhoneCall(order.customerInfo?.phone)}
                                                                className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition duration-200"
                                                                title="Call via Port SIP"
                                                            >
                                                                <Phone size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleWhatsApp(order.customerInfo?.phone)}
                                                                className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition duration-200"
                                                                title="WhatsApp"
                                                            >
                                                                <MessageCircle size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => { setSelectedOrder(order); markOrder(order.id); }}
                                                                className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition duration-200"
                                                                title="Add Note"
                                                            >
                                                                <Package size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleShowNotes(order)}
                                                                className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition duration-200"
                                                                title="View Notes"
                                                            >
                                                                <FileText size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => setProfileOrder(order)}
                                                                className="relative p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition duration-200"
                                                                title="Customer Profile, পছন্দের Product & Repeat Reminder"
                                                            >
                                                                <UserRound size={18} />
                                                                {order.customerProfile && (
                                                                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => openCreateOrder(navigate, order)}
                                                                className="p-2 bg-gray-900 text-white hover:bg-gray-700 rounded-lg transition duration-200"
                                                                title="এই কাস্টমারের জন্য Order Create"
                                                            >
                                                                <PlusCircle size={18} />
                                                            </button>
                                                            <CallHistoryButton order={order} />
                                                            <button
                                                                onClick={() => setReviewOrder(order)}
                                                                className="relative p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition duration-200"
                                                                title={(order.cartItems || []).length > 0 && (order.cartItems || []).every(it => it.review_added) ? 'Review added — add another' : 'Add Review'}
                                                            >
                                                                <Star size={18} />
                                                                {(order.cartItems || []).length > 0 && (order.cartItems || []).every(it => it.review_added) && (
                                                                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Pagination */}
                            <div className="mt-6 flex flex-wrap justify-between items-center gap-3">
                                <button
                                    onClick={handlePrevPage}
                                    disabled={page === 1}
                                    className="px-6 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div className="text-gray-500">
                                    Page <span className="font-semibold text-gray-900">{page}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                                </div>
                                <button
                                    onClick={handleNextPage}
                                    disabled={page >= totalPages}
                                    className="px-6 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}

                    {/* Add Review Modal */}
                    {reviewOrder && (
                        <AddReviewModal
                            order={reviewOrder}
                            onClose={() => setReviewOrder(null)}
                            onSaved={handleReviewSaved}
                        />
                    )}

                    {/* Customer Profile Modal */}
                    {profileOrder && (
                        <CustomerProfileModal
                            order={profileOrder}
                            onClose={() => setProfileOrder(null)}
                            onSaved={handleProfileSaved}
                        />
                    )}

                    {/* Note Modal */}
                    {selectedOrder && !showNotesModal && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl border border-gray-200">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-2xl font-bold">Add Call Note</h2>
                                    <button
                                        onClick={() => {
                                            setSelectedOrder(null);
                                            setNoteText('');
                                            setLeadStatus('');
                                            setNextCallDate('');
                                        }}
                                        className="text-gray-500 hover:text-gray-900"
                                    >
                                        <X size={24} />
                                    </button>
                                </div>

                                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-700">
                                        Customer: <span className="font-semibold">{selectedOrder.customerInfo?.name}</span>
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Phone: <span className="font-semibold">{selectedOrder.customerInfo?.phone}</span>
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Order ID: <span className="font-mono text-orange-600">{selectedOrder.orderId}</span>
                                    </p>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Lead Status *</label>
                                    <select
                                        value={leadStatus}
                                        onChange={(e) => setLeadStatus(e.target.value)}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                    >
                                        <option value="">Select Status</option>
                                        <option value="followup">Follow Up</option>
                                        <option value="converted">Converted</option>
                                        <option value="next_call">Next Call</option>
                                        <option value="not_interested">Not Interested</option>
                                    </select>
                                </div>

                                <NextCallPicker value={nextCallDate} onChange={setNextCallDate} />

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Note *</label>
                                    <textarea
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 h-32 resize-none"
                                        placeholder="Enter your call notes here..."
                                    />
                                </div>

                                <button
                                    onClick={saveCallNote}
                                    disabled={!noteText.trim()}
                                    className="w-full flex items-center justify-center gap-2 bg-orange-400 text-white hover:bg-orange-500  font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Save size={20} />
                                    Save Note
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Notes History Modal */}
                    {showNotesModal && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                            <div className="bg-white rounded-2xl p-6 w-full max-w-3xl border border-gray-200 max-h-[80vh] overflow-y-auto">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-2xl font-bold">Call Notes History</h2>
                                    <button
                                        onClick={() => {
                                            setShowNotesModal(false);
                                            setSelectedOrder(null);
                                            setCallNotes([]);
                                        }}
                                        className="text-gray-500 hover:text-gray-900"
                                    >
                                        <X size={24} />
                                    </button>
                                </div>

                                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-700">
                                        Customer: <span className="font-semibold">{selectedOrder?.customerInfo?.name}</span>
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Phone: <span className="font-semibold">{selectedOrder?.customerInfo?.phone}</span>
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Order ID: <span className="font-mono text-orange-600">{selectedOrder?.orderId}</span>
                                    </p>
                                </div>

                                {loadingNotes ? (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500"></div>
                                    </div>
                                ) : callNotes.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500">No notes found for this order</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {callNotes.map((note) => (
                                            <div key={note.id} className="bg-gray-50 rounded-lg p-4 border border-gray-300">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <p className="text-sm text-gray-500">
                                                            By: <span className="text-gray-900 font-semibold">{note.email}</span>
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {formatDate(note.timestamp)}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {formatFullDate(note.timestamp)}
                                                        </p>
                                                    </div>
                                                    {note.leadStatus && getLeadStatusBadge(note.leadStatus)}
                                                </div>
                                                <p className="text-gray-900 mt-2 whitespace-pre-wrap">{note.note}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DeliveredOrderPage;
